import type { Locale } from "../localization/constants";
import { resolveLocalizedString } from "../localization";
import { compareCatalogCodes } from "./compareCatalogCodes";

export type EventContentCatalogUnit = {
  id: string;
  code: string;
  name: Record<string, string>;
  unlockMode: string;
  accessCode: string;
};

export type EventContentCatalogLevel = {
  id: string;
  code: string;
  order: number;
  modality: string;
  unitId: string;
  difficulty?: string;
  playFormat?: string;
  storyRef?: { id: string };
};

export type EventContentCatalogStory = {
  id: string;
  code: string;
  title: Record<string, string>;
};

export type StoredLevelStats = {
  completedCount: number;
  inProgressCount: number;
  starCounts: [number, number, number, number];
  avgScore: number | null;
  scoreSampleCount: number;
  avgDurationSeconds: number | null;
  durationSampleCount: number;
  participantCount: number;
};

export type LevelFunnelStats = {
  notStarted: number;
  inProgress: number;
  completed: number;
  started: number;
  startedPercent: number;
  completedPercent: number;
  avgStars: number | null;
  avgScore: number | null;
  avgDurationSeconds: number | null;
  durationSampleCount: number;
  starDistribution: [number, number, number, number];
};

export type EventContentLevelRow = {
  levelId: string;
  levelCode: string;
  order: number;
  modality: string;
  difficulty?: string;
  playFormat?: string;
  storyTitle?: string;
  stats: LevelFunnelStats;
};

export type EventContentUnitSection = {
  unitId: string;
  unitCode: string;
  unitName: string;
  unlockMode: string;
  accessCode: string;
  statsLoaded: boolean;
  levels: EventContentLevelRow[];
};

export type EventContentOverview = {
  participantCount: number;
  statsLoaded: boolean;
  units: EventContentUnitSection[];
};

export type AggregateEventContentOverviewInput = {
  units: EventContentCatalogUnit[];
  levels: EventContentCatalogLevel[];
  stories: EventContentCatalogStory[];
  statsByLevelId: ReadonlyMap<string, StoredLevelStats>;
  locale: Locale;
};

function percent(n: number, d: number): number {
  return d > 0 ? Math.max(0, Math.min(100, Math.round((n / d) * 100))) : 0;
}

function compareLevels(
  a: { order: number; code: string },
  b: { order: number; code: string }
): number {
  const cmp = a.order - b.order;
  if (cmp !== 0) {
    return cmp;
  }
  return compareCatalogCodes(a.code, b.code);
}

function emptyStats(participantCount: number): LevelFunnelStats {
  return {
    notStarted: participantCount,
    inProgress: 0,
    completed: 0,
    started: 0,
    startedPercent: 0,
    completedPercent: 0,
    avgStars: null,
    avgScore: null,
    avgDurationSeconds: null,
    durationSampleCount: 0,
    starDistribution: [0, 0, 0, 0],
  };
}

function averageStars(
  starCounts: [number, number, number, number]
): number | null {
  let weighted = 0;
  let count = 0;
  for (let stars = 0; stars < starCounts.length; stars += 1) {
    const n = starCounts[stars] ?? 0;
    weighted += stars * n;
    count += n;
  }
  return count > 0 ? weighted / count : null;
}

export function funnelFromStoredStats(
  stored: StoredLevelStats | undefined
): LevelFunnelStats {
  if (!stored) {
    return emptyStats(0);
  }

  const participantCount = stored.participantCount;
  const inProgress = stored.inProgressCount;
  const completed = stored.completedCount;
  const started = inProgress + completed;
  const notStarted = Math.max(0, participantCount - started);

  return {
    notStarted,
    inProgress,
    completed,
    started,
    startedPercent: percent(started, participantCount),
    completedPercent: percent(completed, participantCount),
    avgStars: averageStars(stored.starCounts),
    avgScore: stored.avgScore,
    avgDurationSeconds: stored.avgDurationSeconds,
    durationSampleCount: stored.durationSampleCount,
    starDistribution: stored.starCounts,
  };
}

export function aggregateEventContentOverview({
  units,
  levels,
  stories,
  statsByLevelId,
  locale,
}: AggregateEventContentOverviewInput): EventContentOverview {
  const storyById = new Map(stories.map((story) => [story.id, story]));
  const statsLoaded = statsByLevelId.size > 0;
  let participantCount = 0;
  for (const stats of statsByLevelId.values()) {
    participantCount = stats.participantCount;
    break;
  }

  const sortedUnits = [...units].sort((a, b) =>
    compareCatalogCodes(a.code, b.code)
  );

  const unitSections: EventContentUnitSection[] = sortedUnits.map((unit) => {
    const unitLevels = levels
      .filter((level) => level.unitId === unit.id)
      .sort(compareLevels);

    const levelRows: EventContentLevelRow[] = unitLevels.map((level, index) => {
      const story = level.storyRef?.id
        ? storyById.get(level.storyRef.id)
        : undefined;
      const storyTitle = story
        ? resolveLocalizedString(story.title, locale)
        : undefined;
      const order = level.order > 0 ? level.order : index + 1;

      return {
        levelId: level.id,
        levelCode: level.code,
        order,
        modality: level.modality,
        difficulty: level.difficulty,
        playFormat: level.playFormat,
        storyTitle: storyTitle || undefined,
        stats: statsLoaded
          ? funnelFromStoredStats(statsByLevelId.get(level.id))
          : emptyStats(participantCount),
      };
    });

    return {
      unitId: unit.id,
      unitCode: unit.code,
      unitName: resolveLocalizedString(unit.name, locale) || unit.code,
      unlockMode: unit.unlockMode,
      accessCode: unit.accessCode.trim(),
      statsLoaded,
      levels: levelRows,
    };
  });

  return {
    participantCount,
    statsLoaded,
    units: unitSections,
  };
}
